import { Injectable, Injector } from "@angular/core";
import { Store } from "@ngrx/store";
import { BehaviorSubject } from "rxjs";
import { io, Socket } from "socket.io-client";
import { environment } from "src/environments/environment";
import { discussionSocketEvents } from "../../global/constants";
import { updateUserStatus } from "../../ngrx/action/user-state.actions";
import { UserState } from "../../ngrx/reducer/user-state.reducer";
import { LocalStorageService } from "../storage-service/local-storage.service";
import { AuthService } from "../auth-service/auth.service";

@Injectable({
  providedIn: "root",
})
export class DiscussionSocketService {
  public newMessage = new BehaviorSubject({});
  public newMessageObservable = this.newMessage.asObservable();

  public readMessage = new BehaviorSubject({});
  public readMessageObservable = this.readMessage.asObservable();

  public socket?: Socket;
  private connecting = false;
  private currentAuthToken = "";
  onlineUsers: any = [];
  private subscribedHandlers: Map<string, Set<(...args: any[]) => void>> =
    new Map();
  constructor(
    private localStorageService: LocalStorageService,
    private store: Store<UserState>,
    private injector: Injector
  ) {}

  private authService = this.injector.get(AuthService);

  //Create connection with socket server.
  public createSocketConnection(forceReconnect = false) {
    const authToken = this.localStorageService.getLocalStore("authorization");
    const bearerToken = authToken ? `Bearer ${authToken}` : "";
    const hasValidToken = Boolean(authToken && authToken !== "{}");
    if (!hasValidToken) {
      this.closedSocketConnection();
      return;
    }

    const sameToken = this.currentAuthToken === bearerToken;
    if (!forceReconnect) {
      if (this.connecting) {
        return;
      }
      if (this.socket?.connected && sameToken) {
        this.rebindSubscribedHandlers();
        return;
      }
    }

    this.closedSocketConnection();
    this.connecting = true;
    this.currentAuthToken = bearerToken;

    this.socket = io(environment.discussionURL, {
      query: {
        authorization: bearerToken,
      },
      extraHeaders: {
        authorization: bearerToken,
      },
      transports: ["websocket", "polling"],
    });

    this.createConnection();
    this.rebindSubscribedHandlers();
  }

  // closed socket connections with server.
  public closedSocketConnection() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = undefined;
    }
    this.connecting = false;
    this.currentAuthToken = "";
  }

  public createConnection() {
    if (!this.socket) {
      return;
    }

    this.socket.off("connect");
    this.socket.on("connect", () => {
      this.connecting = false;
      this.emit(discussionSocketEvents.JOIN_SOCKET, {}, (res: any) => {
        this.onlineUsers = res?.data || [];
        this.store.dispatch(updateUserStatus({ data: this.onlineUsers }));
      });
    });
    this.socket.off("connect_error");
    this.socket.on("connect_error", () => {
      this.connecting = false;
    });
    this.socket.off("disconnect");
    this.socket.on("disconnect", () => {
      this.connecting = false;
    });

    this.subscribe(discussionSocketEvents.COMPANY_ONLINE_OFFLINE, (res: any) => {
      const onlineUsers = [...this.onlineUsers].map((item: any) => {
        return item.user_id === res.user_id
          ? {
              ...item,
              user_state: res.user_state,
              user_state_time: res.user_state_time ?? item.user_state_time,
            }
          : { ...item };
      });
      this.onlineUsers = onlineUsers;
      this.store.dispatch(updateUserStatus({ data: onlineUsers }));
    });
    this.subscribe("logout", () => {
      if (!this.authService.logout) {
        this.authService = this.injector.get(AuthService);
      }
      this.authService.logout();
    });
  }

  // Common event emition
  public emit(event: string, data: any, callback?: Function) {
    if (this.socket) {
      return this.socket.emit(event, data, callback);
    }
  }

  // Subscribe events
  public subscribe(event: string, callback: any) {
    if (!this.subscribedHandlers.has(event)) {
      this.subscribedHandlers.set(event, new Set());
    }
    this.subscribedHandlers.get(event)?.add(callback);

    if (this.socket) {
      // Avoid duplicate registration for the same callback.
      this.socket.off(event, callback);
      return this.socket.on(event, callback);
    }
  }

  // Reconnection
  public async reconnect(forceReconnect = false) {
    this.createSocketConnection(forceReconnect);
  }

  private rebindSubscribedHandlers() {
    if (!this.socket) {
      return;
    }
    this.subscribedHandlers.forEach((callbacks, event) => {
      callbacks.forEach((callback) => {
        this.socket.off(event, callback);
        this.socket.on(event, callback);
      });
    });
  }
}
